import React from 'react';

import { PlanConfig } from '../../services/subscriptionService';

interface QuickSelectGroupProps {
  creditAmount: number;
  setCreditAmount: (amount: number) => void;
  isProcessing: boolean;
}

export const QuickSelectGroup: React.FC<QuickSelectGroupProps> = ({
  creditAmount,
  setCreditAmount,
  isProcessing,
}) => {
  const quickAmounts = [50, 100, 200];

  return (
    <fieldset className="quick-select-group">
      <legend className="top-up-label">Quick amounts</legend>
      <div className="quick-select-grid">
        {quickAmounts.map((amt) => (
          <button
            key={amt}
            className={`quick-select-card ${creditAmount === amt ? 'active' : ''}`}
            onClick={() => setCreditAmount(amt)}
            disabled={isProcessing}
            aria-pressed={creditAmount === amt}
            type="button"
          >
            <span className="amount">{amt}</span>
            <span className="unit">Credits</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
};

interface CustomInputGroupProps {
  creditAmount: number;
  setCreditAmount: (amount: number) => void;
  isProcessing: boolean;
  estimatedPrice: React.ReactNode;
  inputId: string;
  currency: string;
  setCurrency: (currency: string) => void;
}

interface BillingCurrencySelectProps {
  currency: string;
  setCurrency: (currency: string) => void;
  disabled?: boolean;
  id?: string;
}

export const BillingCurrencySelect: React.FC<BillingCurrencySelectProps> = ({
  currency,
  setCurrency,
  disabled = false,
  id,
}) => (
  <select
    id={id}
    value={currency}
    onChange={(event) => setCurrency(event.target.value)}
    className="currency-dropdown"
    disabled={disabled}
    aria-label="Billing currency"
  >
    <option value="USD">USD ($)</option>
    <option value="INR">INR (₹)</option>
  </select>
);

export const CustomInputGroup: React.FC<CustomInputGroupProps> = ({
  creditAmount,
  setCreditAmount,
  isProcessing,
  estimatedPrice,
  inputId,
  currency,
  setCurrency,
}) => (
  <div className="custom-input-group">
    <div className="custom-input-heading">
      <label htmlFor={inputId} className="top-up-label">Credits to add</label>
      <div className="custom-input-meta">
        <div className="price-estimation" aria-live="polite">
          Estimated cost <span className="cost-value">{estimatedPrice}</span>
        </div>
        <BillingCurrencySelect
          currency={currency}
          setCurrency={setCurrency}
          disabled={isProcessing}
        />
      </div>
    </div>
    <div className="input-with-button">
      <div className="input-wrapper">
        <input
          id={inputId}
          type="number"
          min="1"
          value={creditAmount}
          onChange={(e) => setCreditAmount(Math.max(1, parseInt(e.target.value) || 0))}
          className="credit-input"
          disabled={isProcessing}
        />
        <span className="input-suffix">Credits</span>
      </div>
    </div>
  </div>
);

export const CreditPurchaseButton: React.FC<{
  creditAmount: number;
  isProcessing: boolean;
  onTopUp: () => void;
}> = ({ creditAmount, isProcessing, onTopUp }) => (
  <button
    className="top-up-button top-up-button--final"
    onClick={onTopUp}
    disabled={isProcessing || creditAmount <= 0}
    aria-busy={isProcessing}
    type="button"
  >
    {isProcessing ? 'Processing...' : `Buy ${creditAmount} credits`}
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  </button>
);

export const getEstimatedPrice = (
  amount: number, 
  curr: string, 
  loading: boolean,
  plans: Record<string, PlanConfig>,
  planId: string
) => {
  if (loading) return '...';

  const plan = plans[planId];
  const rate = curr === 'USD' 
    ? (plan?.topup_price_per_credit || 0.34)
    : (plan?.topup_price_per_credit_inr || 29.00);

  const total = amount * rate;

  if (curr !== 'INR') return `$${total.toFixed(2)}`;

  return (
    <span className="price-estimation-wrapper">
      <span className="main-price">₹{total.toFixed(2)}</span>
      <span className="gst-subtext gst-subtext-inline">
         (Includes 18% GST)
      </span>
    </span>
  );
};
